/**
 * Design tokens — the single source of truth for Vqura's visual language.
 *
 * Nothing outside `theme.js` should import raw values from here, and no component
 * should hardcode a colour. Before this file existed the brand pink was typed by
 * hand in seven files and radii were guessed per component; that drift is what
 * made the product look unfinished.
 *
 * PINK DISCIPLINE: brand pink marks *actions and the current selection*. Structure,
 * text and data render in neutrals. #ea258b on white is ~3.9:1 — it fails WCAG AA
 * for body text, so it is never used for running copy.
 */

// ---------------------------------------------------------------------------
// Brand
// ---------------------------------------------------------------------------
export const brand = {
  50: '#fdf2f8',   // tint: selected rows, subtle fills
  100: '#fce7f3',
  200: '#fbcfe8',
  300: '#f9a8d4',
  400: '#f472b6',
  500: '#ea258b',  // primary action — the existing brand pink
  600: '#d01e76',
  700: '#b81570',  // hover / pressed
  800: '#9d174d',
  900: '#831843',
};

// ---------------------------------------------------------------------------
// Neutrals — carry all structure and text
// ---------------------------------------------------------------------------
export const neutral = {
  0: '#ffffff',
  25: '#fcfcfd',   // page background (replaces the pink-tinted #faf7fa)
  50: '#f9fafb',
  100: '#f2f4f7',
  200: '#e4e7ec',  // borders, dividers
  300: '#d0d5dd',
  400: '#98a2b3',  // disabled text
  500: '#667085',  // secondary text
  600: '#475467',
  700: '#344054',
  800: '#1d2939',  // primary text
  900: '#101828',
};

// ---------------------------------------------------------------------------
// Semantic
// ---------------------------------------------------------------------------
export const semantic = {
  success: { light: '#ecfdf3', main: '#12b76a', dark: '#027a48', contrast: '#ffffff' },
  warning: { light: '#fffaeb', main: '#f79009', dark: '#b54708', contrast: '#ffffff' },
  danger: { light: '#fef3f2', main: '#f04438', dark: '#b42318', contrast: '#ffffff' },
  info: { light: '#eff8ff', main: '#2e90fa', dark: '#175cd3', contrast: '#ffffff' },
};

/**
 * Developmental level colours (L1–L4), promoted out of DomainForm's local
 * `levelColors`. Chosen to stay distinguishable under deuteranopia and to differ
 * in lightness as well as hue, so level is never conveyed by hue alone.
 */
export const level = {
  1: { main: '#2e90fa', light: '#eff8ff', label: 'Level 1' },
  2: { main: '#12b76a', light: '#ecfdf3', label: 'Level 2' },
  3: { main: '#f79009', light: '#fffaeb', label: 'Level 3' },
  4: { main: '#7a5af8', light: '#f4f3ff', label: 'Level 4' },
};

/**
 * Assessment response colours. Y / I (emerging) / N are the product's core
 * vocabulary; they always ship alongside a text label.
 */
export const response = {
  Y: { main: semantic.success.main, light: semantic.success.light, label: 'Yes' },
  I: { main: semantic.warning.main, light: semantic.warning.light, label: 'Emerging' },
  N: { main: neutral[500], light: neutral[100], label: 'No' },
};

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------
export const fontFamily = 'Inter, Roboto, "Segoe UI", -apple-system, Arial, sans-serif';

export const typeScale = {
  display: { fontSize: '2.25rem', lineHeight: 1.2, fontWeight: 700, letterSpacing: '-0.02em' },
  h1: { fontSize: '1.875rem', lineHeight: 1.25, fontWeight: 700, letterSpacing: '-0.02em' },
  h2: { fontSize: '1.5rem', lineHeight: 1.3, fontWeight: 700, letterSpacing: '-0.01em' },
  h3: { fontSize: '1.25rem', lineHeight: 1.4, fontWeight: 600 },
  h4: { fontSize: '1.125rem', lineHeight: 1.4, fontWeight: 600 },
  bodyLg: { fontSize: '1rem', lineHeight: 1.6, fontWeight: 400 },
  body: { fontSize: '0.9375rem', lineHeight: 1.6, fontWeight: 400 },
  bodySm: { fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 400 },
  caption: { fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 500 },
  overline: { fontSize: '0.6875rem', lineHeight: 1.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' },
};

// ---------------------------------------------------------------------------
// Space, radius, elevation, motion
// ---------------------------------------------------------------------------

/** 4px base. MUI's spacing(n) resolves to n * 4px. */
export const spacingBase = 4;

export const radius = {
  xs: 6,   // chips, badges, small pills
  sm: 8,   // buttons, inputs
  md: 10,  // dialogs, alerts, default shape
  lg: 10,  // cards, paper surfaces
  full: 999,
};

/**
 * Three steps, deliberately soft. The previous build mixed MUI elevation 3 with
 * hand-written pink glows like `0 6px 20px #ea258b55`.
 */
export const shadow = {
  sm: '0 1px 2px 0 rgba(16, 24, 40, 0.06)',
  md: '0 4px 8px -2px rgba(16, 24, 40, 0.08), 0 2px 4px -2px rgba(16, 24, 40, 0.04)',
  lg: '0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)',
  focus: `0 0 0 3px ${brand[100]}`,
};

export const motion = {
  fast: '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  base: '180ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '280ms cubic-bezier(0.4, 0, 0.2, 1)',
};

/**
 * Minimum interactive target. 44px is the touch floor — the app is used on a
 * tablet in the room with the child, not only at a desk.
 */
export const touchTarget = 44;

export const layout = {
  appBarHeight: 64,
  drawerWidth: 272,
  contentMaxWidth: 1280,
};
